import React, { useState, useEffect } from 'react';

const FormInput = ({
  label,
  name,
  value,
  onChange,
  placeholder,
  required,
  type = 'text',
  disabled = false,
  readOnly = false,
  className = '',
  ariaLabel,
}) => {
  // -----------------------------------------------------------------
  // 1. Validation & error logic (unchanged)
  // -----------------------------------------------------------------
  const stringValue = value == null ? '' : String(value);
  const showError = required && !disabled && !readOnly && stringValue.trim() === '';

  // -----------------------------------------------------------------
  // 2. Date formatting: D-M-YYYY
  // -----------------------------------------------------------------
  const formatDateDisplay = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';

    const day = date.getDate();
    const month = date.getMonth() + 1; // 0-indexed
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };
  const [displayValue, setDisplayValue] = useState('');

  useEffect(() => {
    if (type === 'date' && value) {
      setDisplayValue(formatDateDisplay(value));
    } else if (type === 'date') {
      setDisplayValue('');
    }
  }, [value, type]);

  // -----------------------------------------------------------------
  // 4. Number handler (unchanged)
  // -----------------------------------------------------------------
    // -----------------------------------------------------------------
  const handleNumberChange = (e) => {
    let raw = e.target.value.replace(/,/g, ''); // Remove existing commas

    // Allow empty, single minus, decimal point, or valid number pattern
    const isValid =
      raw === '' ||
      raw === '-' ||
      raw === '.' ||
      raw === '-.' ||
      /^-?\d*\.?\d*$/.test(raw);

    if (!isValid) return; // Block invalid input immediately

    // Update parent with clean numeric string (no formatting yet)
    onChange({ target: { name, value: raw === '' ? '' : raw } });
  };

  // Format only for display (with thousand separators, preserves decimal zeros)
  const formatNumberDisplay = (val) => {
    if (val === '' || val === '-' || val === '.' || val === '-.') return val;

    const parts = val.toString().split('.');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.length > 1 ? `${integerPart}.${parts[1]}` : integerPart;
  };


  // -----------------------------------------------------------------
  // 5. Date change handler
  // -----------------------------------------------------------------
  const handleDateChange = (e) => {
    const isoValue = e.target.value; // YYYY-MM-DD from native picker
    const display = formatDateDisplay(isoValue);
    setDisplayValue(display);
    onChange({ target: { name, value: isoValue } });
  };


  // -----------------------------------------------------------------
  // 6. Determine input type & value
  // -----------------------------------------------------------------
  let inputType = type;
  let inputValue = stringValue;
  let inputOnChange = onChange;

  if (type === 'number') {
    inputType = 'text';
    inputValue = value == null || value === '' ? '' : formatNumberDisplay(String(value));
    inputOnChange = handleNumberChange;
  } else if (type === 'date') {
    // Use native date picker when possible
    inputType = 'date'; // This gives YYYY-MM-DD picker
    inputValue = value || '';
    inputOnChange = handleDateChange;

    // Optional: fallback to text with D-M-YYYY if you want custom input
    // Uncomment below to force text input with formatting:
    /*
    inputType = 'text';
    inputValue = displayValue;
    inputOnChange = handleDateTextChange;
    placeholder = placeholder || 'D-M-YYYY';
    */
  }

  // -----------------------------------------------------------------
  // 7. Input props
  // -----------------------------------------------------------------
  const inputProps = {
    type: inputType,
    name,
    value: inputValue,
    onChange: inputOnChange,
    placeholder,
    disabled,
    readOnly,
    className: `${className} border rounded px-3 py-2 focus:outline-none focus:ring-2 ${
      showError
        ? 'border-darkred focus:ring-darkred'
        : 'border-gray-300 focus:ring-primary'
    } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`.trim(),
    'aria-label': ariaLabel || label,
  };

  // -----------------------------------------------------------------
  // 8. Render
  // -----------------------------------------------------------------
  return (
    <div className="flex flex-col space-y-1">
      <label className="font-medium text-gray-700 flex items-center">
        {label}
        {required && !disabled && !readOnly && (
          <span className="ml-1 text-darkred text-sm animate-pulse" title="Required">
            *
          </span>
        )}
      </label>
      <input {...inputProps} />
      {showError && (
        <span className="text-xs text-darkred flex items-center">
          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10A8 8 0 11 2 10a8 8 0 0116 0zm-7-4a1 1 0 10-2 0v3a1 1 0 002 0V6zm-1 7a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"
              clipRule="evenodd"
            />
          </svg>
          This field is required
        </span>
      )}
    </div>
  );
};

export default FormInput;